import {
  call,
  ForkEffect,
  select,
  takeLatest,
  takeEvery,
} from "redux-saga/effects";

import { commonActions } from "@common/redux/common/actions";

import { CommonRepository } from "@common/domain/repositories/CommonRepository";
import {
  fetchedAddressByPincode,
  IExecuteFunctionInStringAction,
  IFetchAddressByPincodeAction,
  IFetchCityAction,
  IFetchCountryAction,
  IFetchStateAction,
} from "@common/redux/common/interface";
import { stringToExecutableFunction } from "@presentation/FormBuilder/components/DynamicOptionsSetup/utils";
import { getAuthSessionTokenSelector } from "../auth/selectors";

function* fetchAddressByPincodeSaga(action: IFetchAddressByPincodeAction) {
  const { onSuccessFetchAddress, onFailureFetchAddress, pincode } =
    action.payload;
  try {
    const response = yield call(
      CommonRepository.fetchAddressByPincode,
      pincode
    );
    const addresses: fetchedAddressByPincode[] = response.PostOffice;
    onSuccessFetchAddress(addresses);
  } catch (error) {
    onFailureFetchAddress(error.message);
  }
}

const updateReferenceValue = (value: string, referenceValue) => {
  return value.replace("{{reference_value}}", referenceValue);
};

function* executeFunctionInString(action: IExecuteFunctionInStringAction) {
  const {
    onSuccess,
    onFailure,
    dynamicApiUri,
    dataConversionFunction,
    referenceValue,
  } = action.payload;
  try {
    const url = new URL(updateReferenceValue(dynamicApiUri, referenceValue));
    const payload = {
      baseUrl: url?.origin,
      urlEndpoint: url?.pathname + url?.search,
    };
    const response = yield call(
      CommonRepository.fetchDataFromDynamicUrl,
      payload
    );
    let data = response;

    if (dataConversionFunction) {
      const executableTransformer = stringToExecutableFunction(
        updateReferenceValue(dataConversionFunction, referenceValue)
      );
      const authToken: string = yield select(getAuthSessionTokenSelector);

      const options = {
        authToken: authToken,
      };
      data = executableTransformer(response, options);
    }

    onSuccess(data);
  } catch (error) {
    onFailure(error.message);
  }
}

function* fetchCountrySaga(action: IFetchCountryAction) {
  const { onSuccess, onFailure } = action.payload;
  try {
    const response = yield call(CommonRepository.fetchCountry);
    onSuccess(response);
  } catch (error) {
    onFailure(error.message);
  }
}

function* fetchStateSaga(action: IFetchStateAction) {
  const { onSuccess, onFailure, country } = action.payload;
  try {
    const response = yield call(
      CommonRepository.fetchState,
      country
    );
    onSuccess(response);
  } catch (error) {
    onFailure(error.message);
  }
}

function* fetchCitySaga(action: IFetchCityAction) {
  const { onSuccess, onFailure, state , country} = action.payload;
  try {
    const response = yield call(CommonRepository.fetchCity, state, country);
    onSuccess(response);
  } catch (error) {
    onFailure(error.message);
  }
}

export function* watchCommon(): Generator<ForkEffect> {
  yield takeLatest(
    commonActions.FETCH_ADDRESS_BY_PINCODE,
    fetchAddressByPincodeSaga
  );
  yield takeEvery(
    commonActions.EXECUTE_FUNCTION_IN_STRING,
    executeFunctionInString
  );
  yield takeLatest(commonActions.FETCH_COUNTRY, fetchCountrySaga);
  yield takeLatest(commonActions.FETCH_STATE, fetchStateSaga);
  yield takeLatest(commonActions.FETCH_CITY, fetchCitySaga);
}
